/**
 * EditAgentConnectionDialog — first-class edit surface for an agent connection
 * stored under `agentConnections` in `connections.json` (or the draft file in
 * draft mode).
 *
 * Reuses `AgentConnectionForm` seeded from the persisted `AgentConnection`.
 * The agent form is a controlled component (values + onChange owned by the
 * parent), so this dialog manages the form state, dirty tracking, and
 * discard-confirm chrome. Persistence delegates to the caller-supplied
 * `onSave`, typically `useConnectionsEditor.handleSaveLocalConnection`, so
 * draft/prod routing stays authoritative.
 *
 * Scope notes:
 * - No OAuth re-authorization for GitHubCopilot in this pass — the connection
 *   already has a valid token from creation. Users can rotate via delete +
 *   recreate if needed. Renaming is still supported.
 * - No app-settings extraction for the API key on save — the value is
 *   round-tripped as-is. If the initial value was `@appsetting(NAME)`, that
 *   reference is preserved when the user leaves the field untouched. If the
 *   user types over it, the new value is written inline. Consistent with the
 *   MVP scope of Phase B; a full rotation UX can layer on later.
 * - Subscription/account/project ARM IDs can be re-selected via the cascading
 *   dropdowns, but changing them effectively re-targets the connection and is
 *   an advanced operation. The form's normal validation applies.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AgentConnectionForm } from '@/components/WorkflowDesigner/NodeEditor/connections/forms/AgentConnectionForm';
import type { AgentConnectionFormValues, AgentModelType } from '@/types/agentConnection';
import { isAgentConnectionFormValid, isFoundryModelType } from '@/utils/connections';
import type { AgentConnection } from '@/types/logicApps';
import { DiscardChangesDialog } from './editForm/DiscardChangesDialog';
import { useDiscardGuard } from './editForm/useDiscardGuard';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '720px',
    minWidth: '560px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: '360px',
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface EditAgentConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  connectionKey: string;
  connection: AgentConnection;
  onSave: (recordKey: string, record: AgentConnection) => Promise<void>;
}

/** Extract the `{subId}` segment from an ARM resource ID, if present. */
function parseSubscriptionId(armId?: string): string | undefined {
  if (!armId) return undefined;
  const m = armId.match(/\/subscriptions\/([^/]+)/i);
  return m?.[1];
}

/**
 * Infer the `AgentModelType` from a persisted `AgentConnection`. Prefers the
 * explicit `type` field (which `handleCreateAgentConnection` writes as the
 * concrete provider name), then falls back to `modelProvider.type`, then to
 * endpoint-based heuristics. When nothing matches we default to
 * `V1ChatCompletionsService` — the most permissive edit surface (manual
 * endpoint + key) so the dialog is at least openable.
 */
function inferAgentModelType(conn: AgentConnection): AgentModelType {
  const t = conn.type ?? conn.modelProvider?.type;
  if (
    t === 'AzureOpenAI' ||
    t === 'FoundryAgentService' ||
    t === 'FoundryAgentServiceV2' ||
    t === 'APIMGenAIGateway' ||
    t === 'GitHubCopilot' ||
    t === 'V1ChatCompletionsService'
  ) {
    return t;
  }
  const endpoint = conn.endpoint?.toLowerCase() ?? '';
  if (endpoint.includes('githubcopilot')) return 'GitHubCopilot';
  if (endpoint.includes('services.ai.azure.com')) return 'FoundryAgentService';
  if (endpoint.includes('openai.azure.com')) return 'AzureOpenAI';
  return 'V1ChatCompletionsService';
}

/**
 * Seed `AgentConnectionFormValues` from the persisted connection. Maps
 * `resourceId` to `accountId` (AzureOpenAI) or `projectId` (Foundry) so the
 * cascading dropdowns render pre-selected.
 */
function seedFormValues(
  conn: AgentConnection,
  modelType: AgentModelType,
): AgentConnectionFormValues {
  // With `exactOptionalPropertyTypes: true`, optional fields must be omitted
  // (not set to `undefined`), so build the object incrementally.
  const base: AgentConnectionFormValues = { displayName: conn.displayName ?? '' };
  if (conn.endpoint !== undefined) base.endpoint = conn.endpoint;
  if (conn.authentication?.key !== undefined) base.apiKey = conn.authentication.key;

  const subscriptionId = parseSubscriptionId(conn.resourceId);
  if (subscriptionId !== undefined) base.subscriptionId = subscriptionId;

  if (modelType === 'AzureOpenAI') {
    if (conn.resourceId !== undefined) base.accountId = conn.resourceId;
  } else if (isFoundryModelType(modelType)) {
    if (conn.resourceId !== undefined) base.projectId = conn.resourceId;
  } else if (modelType === 'APIMGenAIGateway') {
    base.apimAuthType =
      conn.authentication?.type === 'ManagedServiceIdentity' ? 'managedIdentity' : 'apiKey';
  }
  return base;
}

/**
 * Fold updated form values back into the original `AgentConnection`. Fields
 * that don't have a first-class control (`modelProvider`, `modelConfiguration`,
 * `parameterValues`) are preserved verbatim from the original record.
 */
function buildAgentConnection(
  values: AgentConnectionFormValues,
  modelType: AgentModelType,
  original: AgentConnection,
): AgentConnection {
  const updated: AgentConnection = {
    ...original,
    displayName: values.displayName,
  };
  if (values.endpoint !== undefined) {
    updated.endpoint = values.endpoint;
  }
  if (modelType === 'AzureOpenAI' && values.accountId) {
    updated.resourceId = values.accountId;
  } else if (isFoundryModelType(modelType) && values.projectId) {
    updated.resourceId = values.projectId;
  }

  // Preserve managed-identity / None auth as-is (nothing to edit for them).
  // For key-based auth, write back whatever the field holds — the initial
  // seed carried through either the raw key or the `@appsetting(NAME)`
  // reference from the create flow, so a no-op edit round-trips losslessly.
  const authType = original.authentication?.type;
  if (authType === 'ManagedServiceIdentity' || authType === 'None') {
    // Guard for `exactOptionalPropertyTypes`: only assign when defined.
    if (original.authentication !== undefined) {
      updated.authentication = original.authentication;
    }
  } else if (modelType === 'APIMGenAIGateway' && values.apimAuthType === 'managedIdentity') {
    updated.authentication = { type: 'ManagedServiceIdentity' };
  } else if (values.apiKey !== undefined) {
    updated.authentication = {
      ...(original.authentication ?? {}),
      type: 'Key',
      key: values.apiKey,
    };
  }
  return updated;
}

export const EditAgentConnectionDialog = ({
  open,
  onClose,
  connectionKey,
  connection,
  onSave,
}: EditAgentConnectionDialogProps) => {
  const styles = useStyles();

  const modelType = useMemo(() => inferAgentModelType(connection), [connection]);
  const initialValues = useMemo(
    () => seedFormValues(connection, modelType),
    [connection, modelType],
  );
  const [values, setValues] = useState<AgentConnectionFormValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [values, initialValues],
  );

  const handleFormChange = useCallback((next: AgentConnectionFormValues) => {
    setValues(next);
    // Clear stale field errors as the user types — matches `EditConnectionDialog`.
    setErrors({});
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);

    // Reuse the create-flow's validity check. It already branches per
    // model type and requires displayName + the fields relevant to the
    // provider (subscription/account for AzureOpenAI, endpoint/key for
    // manual entry, etc.). GHCP is a special case here: the create flow
    // requires OAuth completion, but edit mode doesn't re-auth, so we only
    // need displayName to be non-empty.
    const displayNameOk = !!values.displayName.trim();
    const providerOk =
      modelType === 'GitHubCopilot'
        ? true
        : isAgentConnectionFormValid(modelType, values);
    if (!displayNameOk || !providerOk) {
      const fieldErrors: Record<string, string> = {};
      if (!displayNameOk) fieldErrors.displayName = 'Connection name is required';
      if (!providerOk) {
        if (modelType === 'AzureOpenAI') {
          if (!values.subscriptionId) fieldErrors.subscriptionId = 'Subscription is required';
          if (!values.accountId) fieldErrors.accountId = 'Azure OpenAI resource is required';
        } else if (isFoundryModelType(modelType)) {
          if (!values.subscriptionId) fieldErrors.subscriptionId = 'Subscription is required';
          if (!values.projectId) fieldErrors.projectId = 'AI Foundry project is required';
          if (!values.endpoint) fieldErrors.endpoint = 'Project endpoint is required';
        } else {
          if (!values.endpoint) fieldErrors.endpoint = 'Endpoint URL is required';
          if (values.apimAuthType !== 'managedIdentity' && !values.apiKey) {
            fieldErrors.apiKey = 'API key is required';
          }
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setIsSaving(true);
    try {
      const updated = buildAgentConnection(values, modelType, connection);
      await onSave(connectionKey, updated);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save connection');
    } finally {
      setIsSaving(false);
    }
  }, [values, modelType, connection, onSave, connectionKey, onClose]);

  // Agent dialog has no form snapshot to restore, so no `onDiscard` cleanup —
  // confirming discard simply closes.
  const discardGuard = useDiscardGuard({ isDirty, isBusy: isSaving, onClose });

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={discardGuard.handleOpenChange}
      modalType="modal"
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>Edit agent connection</DialogTitle>
          <DialogContent className={styles.body}>
            <AgentConnectionForm
              agentModelType={modelType}
              values={values}
              onChange={handleFormChange}
              errors={errors}
              disabled={isSaving}
              // Edit mode: never let the AzureOpenAI section auto-fetch the
              // account's endpoint/key from ARM on mount. The persisted values
              // (which may be `@appsetting(...)` refs) are the source of truth
              // until the user explicitly changes something; auto-filling
              // would rewrite them and flip the form's dirty state before the
              // user has done anything.
              autoFillCredentials={false}
              // GHCP already-authenticated: skip the OAuth button and show the
              // "already signed in" preview. Edit mode never re-authorizes.
              isOAuthComplete={modelType === 'GitHubCopilot'}
            />
            {saveError && (
              <div className={styles.errorBanner} role="alert">
                {saveError}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={discardGuard.requestClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/* Discard-confirmation dialog — shared across every ConnectionList
        edit surface for consistent copy + affordances. */}
    <DiscardChangesDialog
      open={discardGuard.showDiscardConfirm}
      onKeepEditing={discardGuard.keepEditing}
      onDiscard={discardGuard.confirmDiscard}
    />
    </>
  );
};

EditAgentConnectionDialog.displayName = 'EditAgentConnectionDialog';
